import { createContext, useContext } from 'react';
import i18n from '../../../i18n';
import { callMethod } from '../../../lib/api';
import { INSIGHTS_AGENT_NAME } from '../lib/agentBranding';
import type { FleetRestaurant } from '../types';
import type { GrowthBrand } from './growthProperties';

/**
 * Growth's demo group: Linden Group, a fictional restaurant group with
 * synthetic reservations and Google data, so every Growth section can be
 * shown where a customer's own sources are missing. It is entered only on
 * purpose (the Data health card or `?demo=group`) and never mixes with real
 * venues: while it is on, every Growth request carries `demo: "linden"`, the
 * server answers from the demo alone, and nothing can be changed, shared or
 * asked of maître d'AI.
 */
export const GROWTH_DEMO_KEY = 'linden';
/** The value of the `demo` URL parameter that opens the demo group. */
export const GROWTH_DEMO_LINK = 'group';

export type GrowthDemoKey = typeof GROWTH_DEMO_KEY;

/** What the server says about the demo group, on every demo payload. */
export type GrowthDemoInfo = {
  key: GrowthDemoKey;
  group: string;
  fictional: true;
  timezone?: string;
  venues: Array<{ id: string; name: string; city: string; brand: string }>;
  brands: Array<{ id: string; name: string; venues: string[] }>;
};

/** Set by GrowthView while the demo group is on; every Growth data hook reads it. */
export const GrowthDemoContext = createContext<GrowthDemoKey | null>(null);

export function useGrowthDemo(): GrowthDemoKey | null {
  return useContext(GrowthDemoContext);
}

/** The demo venues as the venue picker and tables name them. */
export function demoCatalog(info: GrowthDemoInfo | null | undefined): FleetRestaurant[] {
  return (info?.venues ?? []).map(venue => ({ id: venue.id, name: venue.name, city: venue.city, region: venue.city }));
}

/** The demo group's brands, offered in the venue picker like scope groups. */
export function demoBrands(info: GrowthDemoInfo | null | undefined): GrowthBrand[] {
  return (info?.brands ?? []).map(brand => ({ id: brand.id, name: brand.name, restaurantIds: [...brand.venues] }));
}

/** A one-off Growth request for the demo group (outside a hook). */
export function callGrowthDemo<T>(method: string, ...args: unknown[]): Promise<T> {
  return callMethod<T>(method, args, undefined, GROWTH_DEMO_KEY);
}

/**
 * What maître d'AI's panel says while the demo group is on screen, or null
 * when it may answer. The demo is fictional and the assistant reads only the
 * person's own venues, so every way into it (the top bar, the side panel, an
 * ask button) says it isn't available rather than answering about them.
 */
export function maitreDemoNotice(
  mode: string,
  demoOn: boolean,
): { title: string; body: string } | null {
  if (mode !== 'growth' || !demoOn) return null;
  return {
    title: i18n.t('growth:demo.assistantOffTitle'),
    body: i18n.t('growth:demo.assistantOffBody', { name: INSIGHTS_AGENT_NAME }),
  };
}
